/**
 * Cloudflare Turnstile, as much of it as a challenged download needs.
 *
 * Turnstile has no npm package worth adding: it is a script tag and three calls on a global.
 * This module owns all of that — loading the script once, finding the global, rendering a
 * widget into an element, and taking it down again — so the dialog above it only ever says
 * "render a widget here with this site key and tell me when it produces a token".
 *
 * Nothing outside this file mentions `window.turnstile` or the script URL.
 */

/** Cloudflare's script. `render=explicit` stops it rendering widgets on its own. */
const SCRIPT_URL = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit'

/**
 * How long to wait for Cloudflare's script before giving up.
 *
 * An ad blocker fires the script's `error` event, but a proxy that swallows the request
 * fires nothing at all, and without a deadline the member watches a spinner that can never
 * resolve. Generous enough not to punish a slow connection.
 */
const SCRIPT_TIMEOUT_MS = 15_000

/** The slice of Cloudflare's global this module uses. */
type TurnstileGlobal = {
  render: (
    container: HTMLElement,
    options: {
      sitekey: string
      callback: (token: string) => void
      'error-callback'?: (code?: string) => void
      'expired-callback'?: () => void
    }
  ) => string | undefined
  remove: (widgetId: string) => void
}

declare global {
  interface Window {
    turnstile?: TurnstileGlobal
  }
}

/** A rendered widget. Taking it down twice, or after Cloudflare forgot it, is safe. */
export type TurnstileWidget = {
  /** Takes the widget down. Call it when the dialog closes, or Cloudflare's iframe leaks. */
  remove: () => void
}

/** What went wrong, for a caller that has to tell the member something. */
export type TurnstileFailure = 'expired' | 'error'

/**
 * The in-flight or finished script load. Memoised so a member who is challenged twice does
 * not fetch the script twice, and so two dialogs mounting at once share one load.
 */
let scriptLoad: Promise<TurnstileGlobal> | null = null

/**
 * Loads Cloudflare's script if it is not loaded yet and resolves with the global.
 *
 * Rejects when the script cannot be fetched, exposes no API, or never answers — an ad
 * blocker, a stalling proxy, an offline member — so the caller can say so rather than
 * showing an empty box forever. A failed load is forgotten along with its dead tag, so the
 * next challenge tries again instead of replaying the failure.
 */
function loadTurnstile(): Promise<TurnstileGlobal> {
  if (typeof window === 'undefined') {
    return Promise.reject(new Error('Turnstile needs a browser'))
  }

  if (window.turnstile) {
    return Promise.resolve(window.turnstile)
  }

  if (scriptLoad) {
    return scriptLoad
  }

  const script = document.createElement('script')

  const load = new Promise<TurnstileGlobal>((resolve, reject) => {
    const timeout = window.setTimeout(
      () => reject(new Error('Turnstile did not load in time')),
      SCRIPT_TIMEOUT_MS
    )

    script.src = SCRIPT_URL
    script.async = true
    script.defer = true

    script.addEventListener('load', () => {
      window.clearTimeout(timeout)

      if (window.turnstile) {
        resolve(window.turnstile)
        return
      }

      reject(new Error('Turnstile loaded without exposing its API'))
    })

    script.addEventListener('error', () => {
      window.clearTimeout(timeout)
      reject(new Error('Turnstile could not be loaded'))
    })

    document.head.appendChild(script)
  })

  scriptLoad = load.catch((error) => {
    // Forget the failure, and the tag that carries it, so a later challenge gets a fresh
    // attempt instead of stacking dead scripts in the head.
    scriptLoad = null
    script.remove()
    throw error
  })

  return scriptLoad
}

/**
 * Renders a widget into `container` and calls `onToken` with the first token it produces.
 *
 * `onToken` fires at most once. Turnstile can solve a challenge again on its own after a
 * token expires, and a second token arriving unasked would start a download nobody
 * requested, so an expired token is reported through `onFailure` and the member decides.
 *
 * `isCancelled` is consulted immediately before rendering. React runs an effect, its
 * cleanup, and the effect again against the *same* container under StrictMode, and
 * Cloudflare refuses to render a second widget into a container that already holds one — so
 * without this a development remount leaves the dialog permanently broken.
 *
 * Resolves once the widget exists; rejects when the script or the render fails.
 */
export async function renderTurnstile(
  container: HTMLElement,
  siteKey: string,
  {
    onToken,
    onFailure,
    isCancelled = () => false,
  }: {
    onToken: (token: string) => void
    onFailure?: (failure: TurnstileFailure) => void
    isCancelled?: () => boolean
  }
): Promise<TurnstileWidget> {
  const turnstile = await loadTurnstile()

  if (isCancelled()) {
    throw new TurnstileCancelled()
  }

  let hasToken = false

  const widgetId = turnstile.render(container, {
    sitekey: siteKey,
    callback: (token) => {
      if (hasToken) {
        return
      }

      hasToken = true
      onToken(token)
    },
    'error-callback': (code) => {
      // The member is told the check could not be loaded either way. The code is the only
      // thing that says why — 110200 is a site key whose allowed domains do not cover this
      // host, 1101xx an unusable key, 3xxxxx and 6xxxxx Cloudflare's own faults — so log it
      // rather than making support guess from a screenshot of the dialog.
      console.error('Turnstile refused the challenge, code:', code ?? 'none given')
      onFailure?.('error')
    },
    'expired-callback': () => onFailure?.('expired'),
  })

  if (widgetId === undefined) {
    throw new Error('Turnstile refused to render the widget')
  }

  return {
    remove: () => {
      // Cleanup runs after React has already detached the container, and Cloudflare
      // complains about a widget it no longer knows. A throw here would escape into a
      // passive effect cleanup and take down more than this dialog.
      try {
        turnstile.remove(widgetId)
      } catch (error) {
        console.debug('Turnstile widget was already gone:', error)
      }
    },
  }
}

/** Thrown when the caller gave up while the script was still loading. Not an error to show. */
export class TurnstileCancelled extends Error {
  constructor() {
    super('The Turnstile widget was cancelled before it rendered')
    this.name = 'TurnstileCancelled'
  }
}
